// frontend/src/components/OpportunityCreateModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-14
// Updated:      2026-09-16
//
// Description:
//   Create Opportunity modal.
//
//   Updates (2026-01-15):
//     • Enables raised_by_contact_id (backend now supports it).
//     • Sends "description" (DB field) instead of "notes".
//     • Contacts are hard-filtered by customer_id and sorted alphabetically.
//     • Resources dropdown filters out REDACTED/invalid resources.
//
//   Responsive pass (2026-09-16):
//     • Modal is viewport-safe and scrollable on narrow/short screens.
//     • Two-column opportunity form collapses cleanly to one column on mobile.
//     • Full-width Name/Description rows remain full-width at all breakpoints.
//     • Errors and long option-related text can wrap without forcing overflow.
//     • Footer actions stack on mobile and remain compact on larger screens.
//     • No customer/contact/resource loading, filtering, payload, or create logic changed.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const ENABLE_RAISED_BY = true; // ✅ backend supports opportunities.raised_by_contact_id now

const STAGES = [
  { value: "qualification", label: "Qualification" },
  { value: "discovery", label: "Discovery" },
  { value: "proposal", label: "Proposal" },
  { value: "negotiation", label: "Negotiation" },
  { value: "closed", label: "Closed" },
];

const STATUSES = [
  { value: "open", label: "Open" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toNumberOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const isNil = (v) => v === null || v === undefined;

const getContactName = (c) => {
  if (!c) return "";
  const full = c.full_name || c.fullName;
  if (full && String(full).trim()) return String(full).trim();
  const first = c.first_name || c.firstName || "";
  const last = c.last_name || c.lastName || "";
  return `${first} ${last}`.trim();
};

const getContactCustomerId = (c) => {
  if (!c) return null;
  return toInt(!isNil(c.customer_id) ? c.customer_id : c.customerId);
};

const isRedactedResource = (r) => {
  if (!r) return true;
  const emp = String(r.employment_status || r.employmentStatus || "").toLowerCase();
  const rt = String(r.resource_type || r.resourceType || "").toLowerCase();
  const fn = String(r.first_name || r.firstName || "").toUpperCase();
  const ln = String(r.last_name || r.lastName || "").toUpperCase();
  return emp === "redacted" || rt === "redacted" || fn === "REDACTED" || ln === "REDACTED";
};

const resourceLabel = (r) => {
  if (!r) return "";
  const first = r.first_name || r.firstName || "";
  const last = r.last_name || r.lastName || "";
  const full = `${first} ${last}`.trim();
  if (full) return full;

  const email = r.email || "";
  if (email) return email;

  const job = r.job_title || r.jobTitle || "";
  if (job) return job;

  const role = r.system_role || r.systemRole || "";
  if (role) return role;

  const ident = r.identifier || "";
  if (ident) return ident;

  const id = toInt(r.id);
  return id ? `Resource #${id}` : "Resource";
};

export default function OpportunityCreateModal({ open, onClose, onCreate }) {
  const { theme } = useTheme();

  // form state
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [raisedByContactId, setRaisedByContactId] = useState("");
  const [ownerResourceId, setOwnerResourceId] = useState("");
  const [stage, setStage] = useState("qualification");
  const [status, setStatus] = useState("open");
  const [probability, setProbability] = useState("");
  const [amount, setAmount] = useState("");
  const [expectedCloseDate, setExpectedCloseDate] = useState("");
  const [description, setDescription] = useState("");

  // data lists
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersErr, setCustomersErr] = useState("");

  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsErr, setContactsErr] = useState("");

  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesErr, setResourcesErr] = useState("");

  const [submitErr, setSubmitErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const overlay = useMemo(
    () => ({
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: open ? "flex" : "none",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: 8,
      boxSizing: "border-box",
    }),
    [open]
  );

  const modal = useMemo(
    () => ({
      width: "min(980px, 100%)",
      maxHeight: "calc(100dvh - 16px)",
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
      background: theme?.card_bg || "#fff",
      borderRadius: 16,
      padding: 16,
      boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
      border: theme?.card_border || "1px solid rgba(0,0,0,0.10)",
      WebkitOverflowScrolling: "touch",
      overscrollBehavior: "contain",
    }),
    [theme]
  );

  const input = {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.12)",
    padding: "10px 12px",
    fontSize: 12,
    outline: "none",
    background: "#fff",
  };

  const label = { fontSize: 12, opacity: 0.75, fontWeight: 800, marginBottom: 6 };

  const reset = () => {
    setName("");
    setCustomerId("");
    setRaisedByContactId("");
    setOwnerResourceId("");
    setStage("qualification");
    setStatus("open");
    setProbability("");
    setAmount("");
    setExpectedCloseDate("");
    setDescription("");
    setContacts([]);
    setCustomersErr("");
    setContactsErr("");
    setResourcesErr("");
    setSubmitErr("");
    setSubmitting(false);
  };

  // ---------- Load customers ----------
  const loadCustomers = useCallback(async () => {
    setCustomersLoading(true);
    setCustomersErr("");
    try {
      try {
        const { data } = await api.get("/api/customers?limit=500&offset=0");
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setCustomers(arr);
        return;
      } catch {
        const { data } = await api.get("/api/clients?limit=500&offset=0");
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setCustomers(arr);
      }
    } catch (e) {
      setCustomersErr(e?.response?.data?.detail || String(e?.message || "Failed to load customers"));
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  // ---------- Load resources (ACTIVE + is_sales=true + not redacted) ----------
  const loadResources = useCallback(async () => {
    setResourcesLoading(true);
    setResourcesErr("");
    try {
      const { data } = await api.get("/api/resources?include_inactive=true&limit=500");
      const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

      const filtered = arr
        .filter((r) => !isRedactedResource(r))
        .filter((r) => r && r.active === true && r.is_sales === true);

      const sorted = [...filtered].sort((a, b) => {
        const an = resourceLabel(a).toLowerCase();
        const bn = resourceLabel(b).toLowerCase();
        if (an < bn) return -1;
        if (an > bn) return 1;
        return (toInt(a?.id) || 0) - (toInt(b?.id) || 0);
      });

      setResources(sorted);

      // if current selection got filtered, clear it
      const chosen = toInt(ownerResourceId);
      if (chosen && !sorted.some((r) => toInt(r?.id) === chosen)) setOwnerResourceId("");
    } catch (e) {
      setResourcesErr(e?.response?.data?.detail || String(e?.message || "Failed to load resources"));
      setResources([]);
    } finally {
      setResourcesLoading(false);
    }
  }, [ownerResourceId]);

  // ---------- Load contacts for selected customer ----------
  const loadContacts = useCallback(
    async (cid) => {
      setContacts([]);
      setContactsErr("");
      if (!cid) return;

      setContactsLoading(true);
      try {
        const candidates = [
          `/api/contacts?customer_id=${cid}&limit=500`,
          `/api/contacts?customerId=${cid}&limit=500`,
          `/api/clients/${cid}/contacts?limit=500`,
          `/api/contacts?limit=500`,
        ];

        let data = null;
        let lastErr = null;

        for (const url of candidates) {
          try {
            const res = await api.get(url);
            data = res.data;
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
          }
        }

        if (lastErr && data == null) throw lastErr;

        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        const filtered = arr.filter((c) => getContactCustomerId(c) === cid);

        filtered.sort((a, b) => {
          const an = (getContactName(a) || "").toLowerCase();
          const bn = (getContactName(b) || "").toLowerCase();
          if (an < bn) return -1;
          if (an > bn) return 1;
          return (toInt(a?.id) || 0) - (toInt(b?.id) || 0);
        });

        setContacts(filtered);

        const chosen = toInt(raisedByContactId);
        if (chosen && !filtered.some((c) => toInt(c?.id) === chosen)) setRaisedByContactId("");
      } catch (e) {
        setContactsErr(e?.response?.data?.detail || String(e?.message || "Failed to load contacts"));
        setContacts([]);
      } finally {
        setContactsLoading(false);
      }
    },
    [raisedByContactId]
  );

  // open lifecycle: fetch lists on open
  useEffect(() => {
    if (!open) return;
    loadCustomers();
    loadResources();
  }, [open, loadCustomers, loadResources]);

  useEffect(() => {
    const cid = toInt(customerId);
    if (!open) return;
    if (!cid) {
      setContacts([]);
      setRaisedByContactId("");
      return;
    }
    loadContacts(cid);
  }, [customerId, open, loadContacts]);

  const customerOptions = useMemo(() => {
    const arr = customers || [];
    const mapped = arr.map((c) => {
      const id = toInt(c?.id);
      const nm = c?.name || c?.company_name || c?.companyName || "";
      return { id, label: nm ? String(nm) : id ? `Customer #${id}` : "Customer" };
    });
    mapped.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
    return mapped;
  }, [customers]);

  const contactOptions = useMemo(() => {
    return (contacts || []).map((c) => {
      const id = toInt(c?.id);
      const nm = getContactName(c) || (id ? `Contact #${id}` : "Contact");
      const email = c?.email ? ` · ${c.email}` : "";
      return { id, label: `${nm}${email}` };
    });
  }, [contacts]);

  const resourceOptions = useMemo(() => {
    return (resources || []).map((r) => ({ id: toInt(r?.id), label: resourceLabel(r) }));
  }, [resources]);

  const canSubmit = useMemo(() => {
    return !!name.trim() && !!toInt(customerId) && !submitting;
  }, [name, customerId, submitting]);

  const buildPayload = useCallback(() => {
    const payload = {
      name: name.trim(),
      customer_id: toInt(customerId),
      stage: stage || "qualification",
      status: status || "open",
    };

    if (description && String(description).trim()) payload.description = String(description).trim();

    const a = toNumberOrNull(amount);
    if (a !== null) payload.amount = a;

    const p = toNumberOrNull(probability);
    if (p !== null) payload.probability = p;

    const d = expectedCloseDate ? String(expectedCloseDate) : "";
    if (d) payload.expected_close_date = d;

    const oid = toInt(ownerResourceId);
    if (oid) payload.owner_resource_id = oid;

    const rcid = toInt(raisedByContactId);
    if (ENABLE_RAISED_BY) payload.raised_by_contact_id = rcid || null;

    return payload;
  }, [name, customerId, stage, status, description, amount, probability, expectedCloseDate, ownerResourceId, raisedByContactId]);

  const handleCreate = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitErr("");
    setSubmitting(true);
    try {
      const payload = buildPayload();
      await (onCreate && onCreate(payload));
      reset();
      onClose && onClose();
    } catch (e) {
      setSubmitErr(e?.response?.data?.detail || String(e?.message || "Create failed"));
      setSubmitting(false);
    }
  }, [canSubmit, buildPayload, onCreate, onClose]);

  return (
    <div style={overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div style={modal}>
        <div className="flex min-w-0 items-center justify-between gap-3">
          <div className="min-w-0 break-words" style={{ fontWeight: 900, fontSize: 16 }}>
            New Opportunity
          </div>

          <button
            type="button"
            onClick={() => onClose && onClose()}
            style={{
              width: 34,
              height: 34,
              borderRadius: 999,
              border: 0,
              cursor: "pointer",
              background: "rgba(0,0,0,0.08)",
              fontWeight: 900,
              flexShrink: 0,
            }}
            title="Close"
          >
            ✕
          </button>
        </div>

        {submitErr ? (
          <div className="break-words" style={{ marginTop: 10, fontSize: 12, color: theme?.danger || "#b91c1c" }}>{submitErr}</div>
        ) : null}

        <div className="mt-3.5 grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
          <div className="min-w-0 md:col-span-2">
            <div style={label}>Name</div>
            <input
              style={input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. M40 Survey – Feb 2026"
            />
          </div>

          <div className="min-w-0">
            <div style={label}>Customer</div>
            <select style={input} value={customerId} onChange={(e) => setCustomerId(e.target.value)} disabled={customersLoading}>
              <option value="">{customersLoading ? "Loading..." : customersErr ? "Failed to load" : "— Select —"}</option>
              {customerOptions.map((o) => (
                <option key={String(o.id)} value={String(o.id)}>
                  {o.label}
                </option>
              ))}
            </select>
            {customersErr ? (
              <div className="break-words" style={{ marginTop: 6, fontSize: 12, color: theme?.danger || "#b91c1c" }}>{customersErr}</div>
            ) : null}
          </div>

          <div className="min-w-0">
            <div style={label}>Raised By (Contact)</div>
            <select
              style={input}
              value={raisedByContactId}
              onChange={(e) => setRaisedByContactId(e.target.value)}
              disabled={!customerId || contactsLoading}
              title="Select the contact who raised this opportunity"
            >
              <option value="">{contactsLoading ? "Loading..." : contactsErr ? "Failed to load" : "— Optional —"}</option>
              {contactOptions.map((o) => (
                <option key={String(o.id)} value={String(o.id)}>
                  {o.label}
                </option>
              ))}
            </select>
            {contactsErr ? (
              <div className="break-words" style={{ marginTop: 6, fontSize: 12, color: theme?.danger || "#b91c1c" }}>{contactsErr}</div>
            ) : null}
          </div>

          <div className="min-w-0">
            <div style={label}>Owner (Sales Resource)</div>
            <select style={input} value={ownerResourceId} onChange={(e) => setOwnerResourceId(e.target.value)} disabled={resourcesLoading}>
              <option value="">
                {resourcesLoading ? "Loading..." : resourcesErr ? "Failed to load" : "— Optional —"}
              </option>

              {(resourceOptions || []).length === 0 && !resourcesLoading && !resourcesErr ? (
                <option value="" disabled>
                  No active sales owners found
                </option>
              ) : null}

              {resourceOptions.map((o) => (
                <option key={String(o.id)} value={String(o.id)}>
                  {o.label}
                </option>
              ))}
            </select>
            {resourcesErr ? (
              <div className="break-words" style={{ marginTop: 6, fontSize: 12, color: theme?.danger || "#b91c1c" }}>{resourcesErr}</div>
            ) : null}
            <div className="break-words" style={{ marginTop: 6, fontSize: 11, opacity: 0.65 }}>
              Only active sales-enabled resources (is_sales=true).
            </div>
          </div>

          <div className="min-w-0">
            <div style={label}>Stage</div>
            <select style={input} value={stage} onChange={(e) => setStage(e.target.value)}>
              {STAGES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-0">
            <div style={label}>Status</div>
            <select style={input} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-0">
            <div style={label}>Probability (%)</div>
            <input style={input} value={probability} onChange={(e) => setProbability(e.target.value)} placeholder="e.g. 35" />
          </div>

          <div className="min-w-0">
            <div style={label}>Amount</div>
            <input style={input} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 12500" />
          </div>

          <div className="min-w-0">
            <div style={label}>Expected Close Date</div>
            <input type="date" style={input} value={expectedCloseDate} onChange={(e) => setExpectedCloseDate(e.target.value)} />
          </div>

          <div className="min-w-0 md:col-span-2">
            <div style={label}>Description / Next Step</div>
            <textarea
              style={{ ...input, minHeight: 120, resize: "vertical" }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What’s the next action? Who’s involved? Any constraints?"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="w-full bg-btnGeneralNormal text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
            style={{ borderRadius: 10, padding: "10px 14px", fontWeight: 900 }}
            onClick={() => {
              reset();
              onClose && onClose();
            }}
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="button"
            className="w-full bg-btnActionNormal text-white hover:bg-btnActionHover active:bg-btnActionClick sm:w-auto"
            style={{ borderRadius: 10, padding: "10px 14px", fontWeight: 900, opacity: canSubmit ? 1 : 0.5 }}
            disabled={!canSubmit}
            onClick={handleCreate}
          >
            {submitting ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
